import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { QualificationItemRow } from '@/components/profile/QualificationItemRow';
import { SECTION_LABELS } from '@/lib/company-qualification';
import type {
  QualificationItem,
  QualificationSectionKey,
  QualificationStatus,
} from '@shared/types/company-qualification';

interface QualificationSectionCardProps {
  sectionKey: QualificationSectionKey;
  items: QualificationItem[];
  onAdd: (text: string) => void;
  onEdit: (itemId: string, text: string) => void;
  onStatus: (itemId: string, status: QualificationStatus) => void;
  onRemove: (itemId: string) => void;
}

export const QualificationSectionCard = ({ sectionKey, items, onAdd, onEdit, onStatus, onRemove }: QualificationSectionCardProps) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const { label, hint } = SECTION_LABELS[sectionKey];

  const add = () => {
    if (!draft.trim()) return;
    onAdd(draft);
    setDraft('');
    setAdding(false);
  };

  return (
    <section className="border-b border-domino-cardBorder pb-4 last:border-b-0">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-domino-ink">{label}</h3>
        {!adding && <Button variant="ghost" size="sm" onClick={() => setAdding(true)}>+ Ajouter</Button>}
      </div>
      <p className="text-xs text-domino-inkSoft">{hint}</p>
      {items.length > 0 ? (
        <ul className="mt-1 divide-y divide-domino-cardBorder/60">
          {items.map((item) => (
            <QualificationItemRow
              key={item.id}
              item={item}
              onEdit={(text) => onEdit(item.id, text)}
              onStatus={(status) => onStatus(item.id, status)}
              onRemove={() => onRemove(item.id)}
            />
          ))}
        </ul>
      ) : !adding ? <p className="mt-2 text-xs text-domino-inkSoft">À compléter.</p> : null}
      {adding && (
        <div className="mt-2">
          <Textarea value={draft} onChange={(event) => setDraft(event.target.value)} rows={2} autoFocus placeholder="Un fait, une réponse, une question ouverte…" className="text-sm" />
          <div className="mt-1 flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setDraft(''); setAdding(false); }}>Annuler</Button>
            <Button size="sm" onClick={add} disabled={!draft.trim()}>Ajouter</Button>
          </div>
        </div>
      )}
    </section>
  );
};
